import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    InputAdornment,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {formatDataIsoBR, formatReais} from "../../../shared/utils/formatters";
import {getMeioLabel, getModalidadeLabel, getSituacaoVersao, MEIOS, MODALIDADES, plural, TRIAL_MAXIMO} from "../planoVersaoUtils";

const Rotulo = ({children}) => <Typography variant="caption" color="text.secondary" display="block">{children}</Typography>;

// O que é do plano e vale para todas as versões: nome, descrição e vitrine. Salva pelo PUT do plano.
function CamposDoPlano({plano, salvando, onSalvar}) {
    const [campos, setCampos] = useState({nome_plano: plano.nome_plano, descricao: plano.descricao || '', vitrine: Boolean(plano.vitrine)});

    useEffect(() => {
        setCampos({nome_plano: plano.nome_plano, descricao: plano.descricao || '', vitrine: Boolean(plano.vitrine)});
    }, [plano.descricao, plano.nome_plano, plano.vitrine]);

    const alterado = campos.nome_plano !== plano.nome_plano || campos.descricao !== (plano.descricao || '') || campos.vitrine !== Boolean(plano.vitrine);

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>Plano</Typography>
                <Typography variant="caption" color="text.secondary">Vale para todas as versões.</Typography>
            </Stack>
            <TextField label="Nome do plano" size="small" value={campos.nome_plano} inputProps={{maxLength: 100}}
                       onChange={(event) => setCampos((atual) => ({...atual, nome_plano: event.target.value}))} />
            <TextField label="Descrição" size="small" value={campos.descricao} multiline minRows={1}
                       onChange={(event) => setCampos((atual) => ({...atual, descricao: event.target.value}))} />
            <FormControlLabel
                control={<Switch checked={campos.vitrine} onChange={(event) => setCampos((atual) => ({...atual, vitrine: event.target.checked}))} />}
                label="Aparece na vitrine e no autocadastro"
            />
            {alterado ? (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => setCampos({nome_plano: plano.nome_plano, descricao: plano.descricao || '', vitrine: Boolean(plano.vitrine)})} disabled={salvando}>Descartar</Button>
                    <Button size="small" variant="contained" disabled={salvando || !campos.nome_plano.trim()}
                            onClick={() => onSalvar({nome_plano: campos.nome_plano.trim(), descricao: campos.descricao.trim(), vitrine: campos.vitrine})}>
                        {salvando ? 'Salvando…' : 'Salvar o plano'}
                    </Button>
                </Stack>
            ) : null}
        </Stack>
    );
}

// Modalidade, meios, trial e preço do rascunho. O trial fica travado sem cartão entre os meios.
function CamposDoRascunho({form, avisoTrial, onMeio, onCampo}) {
    const temCartao = form.meios_aceitos.includes('CARTAO');
    return (
        <Stack spacing={2}>
            <Box>
                <Rotulo>Modalidade</Rotulo>
                <RadioGroup value={form.modalidade} onChange={(event) => onCampo('modalidade', event.target.value)}>
                    {MODALIDADES.map((item) => (
                        <FormControlLabel key={item.value} value={item.value} control={<Radio size="small" />}
                                          label={<span><b>{item.label}.</b> {item.texto}</span>} />
                    ))}
                </RadioGroup>
            </Box>
            <Box>
                <Rotulo>Meios de pagamento aceitos</Rotulo>
                {MEIOS.map((item) => (
                    <FormControlLabel key={item.value} sx={{display: 'flex'}}
                                      control={<Checkbox size="small" checked={form.meios_aceitos.includes(item.value)} onChange={() => onMeio(item.value)} />}
                                      label={<span><b>{item.label}.</b> {item.texto}</span>} />
                ))}
            </Box>
            {avisoTrial ? <Alert severity="warning">O trial de {plural(avisoTrial, 'dia', 'dias')} foi zerado porque o plano deixou de aceitar cartão.</Alert> : null}
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
                <TextField
                    label="Trial"
                    size="small"
                    type="number"
                    value={form.dias_trial}
                    disabled={!temCartao}
                    onChange={(event) => onCampo('dias_trial', event.target.value)}
                    inputProps={{min: 0, max: TRIAL_MAXIMO, step: 1}}
                    InputProps={{endAdornment: <InputAdornment position="end">dias</InputAdornment>}}
                    helperText={temCartao
                        ? 'O cartão é pedido na adesão, e a cobrança começa sozinha no fim do trial.'
                        : 'Trial só com cartão: sem cartão não há como converter com cobrança automática. Marque Cartão para liberar.'}
                />
                <TextField
                    label="Mensalidade base"
                    size="small"
                    type="number"
                    value={form.preco_base_mensal}
                    onChange={(event) => onCampo('preco_base_mensal', event.target.value)}
                    inputProps={{min: 0, step: 10}}
                    InputProps={{startAdornment: <InputAdornment position="start">R$</InputAdornment>, endAdornment: <InputAdornment position="end">por mês</InputAdornment>}}
                />
            </Box>
        </Stack>
    );
}

function ResumoDaVersao({versao}) {
    const linhas = [
        ['Modalidade', `${getModalidadeLabel(versao.modalidade)}. ${MODALIDADES.find((item) => item.value === versao.modalidade)?.texto || ''}`],
        ['Meios aceitos', (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {(versao.meios_aceitos || []).map((meio) => <Chip key={meio} size="small" variant="outlined" label={getMeioLabel(meio)} />)}
            </Stack>
        )],
        ['Trial', versao.dias_trial ? `${plural(versao.dias_trial, 'dia', 'dias')}, com cartão pedido na adesão` : 'Sem trial'],
        ['Mensalidade base', <b>{formatReais(versao.preco_base_mensal)} por mês</b>],
    ];
    if (versao.publicada_em) linhas.push(['Publicada em', formatDataIsoBR(versao.publicada_em)]);
    return (
        <Box component="dl" sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '160px 1fr'}, rowGap: 1.25, columnGap: 2, m: 0}}>
            {linhas.map(([rotulo, valor]) => (
                <React.Fragment key={rotulo}>
                    <Typography component="dt" variant="body2" color="text.secondary">{rotulo}</Typography>
                    <Typography component="dd" variant="body2" sx={{m: 0}}>{valor}</Typography>
                </React.Fragment>
            ))}
        </Box>
    );
}

/*
 * Cartão "Comercial" da versão (protótipo v2): o que é do plano em cima e o que é da versão embaixo. Só o rascunho
 * edita a versão; a publicada aparece só para leitura.
 */
export default function ComercialCard({plano, versao, form, editavel, avisoTrial, salvandoPlano, onSalvarPlano, onMeio, onCampo}) {
    const situacao = getSituacaoVersao(versao.situacao);
    return (
        <Paper variant="outlined" sx={{p: {xs: 2, md: 2.5}}} component="section" aria-label="Comercial">
            <Typography variant="h6" component="h2" fontWeight={700} sx={{mb: 2}}>Comercial</Typography>
            <CamposDoPlano plano={plano} salvando={salvandoPlano} onSalvar={onSalvarPlano} />
            <Divider sx={{my: 2.5}} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1.5}}>
                <Typography variant="subtitle2" fontWeight={700}>Versão {versao.numero}</Typography>
                <Chip size="small" label={situacao.label} color={situacao.color} variant="outlined" />
            </Stack>
            {editavel
                ? <CamposDoRascunho form={form} avisoTrial={avisoTrial} onMeio={onMeio} onCampo={onCampo} />
                : <ResumoDaVersao versao={versao} />}
        </Paper>
    );
}
